import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import './style.css';
import FormCrafterListView from '../../common/FormCrafterFormRenderer/FormCrafterListView';
import SubformNavigation from '../../common/SubformNavigation/SubformNavigation';
import { useTranslation } from 'react-i18next';
import RejectionReasonModal from '../../common/FormApproveComponents/RejectionReasonItem/RejectionReasonModal';
import StyledTabs from '../../common/StyledTabs/StyledTabs';
import StarTab from '../../common/StyledTabs/StarTab';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Grid from 'apollo-react/components/Grid';
import FormActionButtons from './FormActionButtons/FormActionButtons';
import SectionApprovalButton from '../../common/SectionApproval/SectionApprovalButton';
import RequestBadges from '../../common/RequestBadges/RequestBadges';
import useFormReview from '../../../hooks/features/approvals/useFormReview';
import useSubformNavigation from '../../../hooks/ui/navigation/useSubformNavigation';
import useVisibleComments from '../../../hooks/features/comments/useVisibleComments';
import useRequestProgressApi from '../../../hooks/api/requestsProgress/useRequestProgressApi';
import useAssessmentReviewApi from '../../../hooks/api/assessmentReview/useAssessmentReviewApi';
import { usePageTitle } from '../../../contexts/PageTitleContext';
import {
  APPROVAL_TYPE_CODES,
  APPROVAL_TYPE_IDS,
  APPROVAL_TYPE_TO_ACTION,
} from '../../../constants/workflow.constant';
import { REQUEST_STATUS } from '../../../constants/request.constant';
import RequestAuditTrails from '../../common/RequestAuditTrails';
import AdditionalDocuments from '../../common/AdditionalDocuments';
import LinkedStudiesView from '../../common/LinkedStudies/LinkedStudiesView';
import useLinkedStudies from '../../../hooks/features/customer/useLinkedStudies';
import useRequestParentRequests from '../../../hooks/features/customer/useRequestParentRequests';
import ParentRequestInfo from '../../common/ParentRequest/ParentRequestInfo';
import useLinkedSubmissions from '../../../hooks/features/customer/useLinkedSubmissions';
import LinkedSubmissionsView from '../../common/LinkedSubmissions/LinkedSubmissionsView';
import useSubformCommentStatus from '../../../hooks/features/comments/useSubformCommentStatus';
import RequestClarificationWaves from '../../common/RequestClarificationWaves';
import AssessmentReviewersTab from '../../common/AssessmentReviewersTab';
import FormSections from '../../common/FormApproveComponents/FormSections/FormSections';
import EmbeddedHcpProfileForRequest, {
  useHcpProfileRequestMainMenuTabs,
} from '../../Guest/Requests/New_Requests/EmbeddedHcpProfileForRequest';
import { useRequestDetails } from '../../../hooks';
import {
  isLicensingToolMode,
  useLicensingExperienceOptional,
} from '../../../contexts/LicensingExperienceContext';

function formHasAttachProfileSnapshot(form) {
  const flag = form?.attach_end_user_profile_snapshot;
  return flag === true || flag === 1;
}

const FormApprove = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const isRequestActive = location.state?.isRequestActive || false;

  // Feature hook handles all data fetching and state management
  const {
    submittedForms,
    requestProgress,
    children,
    loading,
    activeTab,
    activeForm,
    handleTabChange,
    isFormTab,
    activeFormComments,
    setCommentsForActiveForm,
    canTakeActionOnForm,
    activeFormApprovalStatus,
    handleApproveSection,
    handleUndoApproval,
    isRequestAssigned,
    requestName,
    generatedId,
    requestDateEnded,
    requestOutcomeStatus,
    currentUserEmail,
  } = useFormReview(id);

  // Fetch linked studies for this request
  const {
    linkedStudies,
    linkedStudiesEnabled,
    loading: linkedStudiesLoading,
    error: linkedStudiesError,
  } = useLinkedStudies(requestProgress?.request_id);

  const { mode: licensingExperienceMode } = useLicensingExperienceOptional();
  const requestDetails =
    useRequestDetails(requestProgress?.request_id, 'admin') || {};
  const isParentWorkflow = Boolean(
    requestDetails?.WorkflowVersion?.workflow?.subWorkflowVersions?.length ||
    requestDetails?.WorkflowVersion?.workflow?.subWorkflows?.length
  );
  const canHaveLinkedSubmissions =
    isParentWorkflow && requestOutcomeStatus === REQUEST_STATUS.APPROVED;
  const linkedSubmissions = useLinkedSubmissions(
    requestProgress?.request_id,
    canHaveLinkedSubmissions
  );
  // Only show the tab once there's at least one linked sub-request - an
  // approved parent-workflow request with nothing linked yet doesn't need
  // an empty tab.
  const requestParents = useRequestParentRequests(requestProgress?.request_id);
  const hasParentRequests = requestParents.parentRequests.length > 0;
  const showParentRequestTab =
    (canHaveLinkedSubmissions && linkedSubmissions.total > 0) ||
    hasParentRequests;
  const hcpProfileMainMenuTabs = useHcpProfileRequestMainMenuTabs();
  const [, setFormSectionsComments] = useState([]);

  const attachEndUserProfileSnapshot = useMemo(() => {
    if (!isLicensingToolMode(licensingExperienceMode)) return false;
    if (formHasAttachProfileSnapshot(activeForm)) return true;
    return Boolean(requestDetails?.end_user_profile_snapshot_json);
  }, [licensingExperienceMode, activeForm, requestDetails]);

  const renderHcpProfileMenuContent = useCallback(
    (tabId) => (
      <EmbeddedHcpProfileForRequest
        activeMainMenuTabId={tabId}
        profileDataMode="snapshot"
        requestProfileSnapshotJson={
          requestDetails?.end_user_profile_snapshot_json
        }
        suppressApplicantActionChrome
      />
    ),
    [requestDetails?.end_user_profile_snapshot_json]
  );

  const [assessmentTabData, setAssessmentTabData] = useState(null);

  // Calculate dynamic tab indices based on whether linked studies are enabled
  const dynamicTabs = useMemo(() => {
    const baseIndex = submittedForms.length;
    const hasAssessment = !!assessmentTabData;
    const assessmentTabIndex = hasAssessment ? baseIndex : -1;
    const afterAssessment = baseIndex + (hasAssessment ? 1 : 0);
    const linkedStudiesIndex = linkedStudiesEnabled ? afterAssessment : -1;
    const afterLinkedStudies = afterAssessment + (linkedStudiesEnabled ? 1 : 0);
    const parentRequestIndex = showParentRequestTab ? afterLinkedStudies : -1;
    const afterParentRequest =
      afterLinkedStudies + (showParentRequestTab ? 1 : 0);
    const additionalDocumentsIndex = afterParentRequest;
    const requestAuditTrailsIndex = afterParentRequest + 1;
    const requestClarificationWavesIndex = afterParentRequest + 2;
    return {
      linkedStudiesIndex,
      additionalDocumentsIndex,
      requestAuditTrailsIndex,
      assessmentTabIndex,
      requestClarificationWavesIndex,
      parentRequestIndex,
    };
  }, [
    submittedForms.length,
    linkedStudiesEnabled,
    assessmentTabData,
    showParentRequestTab,
  ]);

  // Open on Relevant Requests by default (once), unless a tab is requested
  const defaultTabAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultTabAppliedRef.current || dynamicTabs.parentRequestIndex === -1) {
      return;
    }
    defaultTabAppliedRef.current = true;
    if (!searchParams.get('tab')) {
      handleTabChange(null, dynamicTabs.parentRequestIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicTabs.parentRequestIndex]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (
      tabParam === 'clarifications' &&
      dynamicTabs.requestClarificationWavesIndex !== -1
    ) {
      handleTabChange(null, dynamicTabs.requestClarificationWavesIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicTabs]);

  // Filter comments to only include those on visible fields
  const { commentStatus: fieldCommentStatus, visibleComments } =
    useVisibleComments({
      comments: activeFormComments,
      subforms: activeForm?.subforms,
      filledForm: activeForm?.filledForm,
    });

  // Fetch subform-level comment status for navigation indicators
  const { subformCommentStatus, refreshSubformCommentStatus } =
    useSubformCommentStatus(requestProgress?.request_id);

  // Merge field-level and subform-level comment statuses
  const visibilityAwareCommentStatus = useMemo(
    () => ({ ...fieldCommentStatus, ...subformCommentStatus }),
    [fieldCommentStatus, subformCommentStatus]
  );

  // API hook for request progress operations
  const { switchProgress, getSendBackTargets } = useRequestProgressApi();
  const { submitAssessmentReview, getAssessmentTab } = useAssessmentReviewApi();

  // Set dynamic page title based on request name
  const { setPageTitle, clearPageTitle } = usePageTitle();

  useEffect(() => {
    if (requestName) {
      setPageTitle(requestName);
    }
    return () => clearPageTitle();
  }, [requestName, setPageTitle, clearPageTitle]);

  useEffect(() => {
    if (!requestProgress?.request_id) return;
    getAssessmentTab(requestProgress.request_id)
      .then((res) =>
        setAssessmentTabData(res.data?.callerRole ? res.data : null)
      )
      .catch(() => setAssessmentTabData(null));
  }, [requestProgress?.request_id, getAssessmentTab]);

  // Local UI state for modals
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sendBackTargets, setSendBackTargets] = useState([]);
  const [isApprovingSection, setIsApprovingSection] = useState(false);

  // Fetch eligible send-back targets
  useEffect(() => {
    if (!requestProgress?.id) return;

    getSendBackTargets(requestProgress.id)
      .then(setSendBackTargets)
      .catch((error) => {
        console.error('Error fetching send-back targets:', error);
        setSendBackTargets([]);
      });
  }, [requestProgress?.id, getSendBackTargets]);

  // Filter children based on send-back eligibility
  const filteredChildren = useMemo(() => {
    if (!children) return [];

    const filtered = children.filter((child) => {
      // Keep approve and reject actions always
      if (child.ApprovalType?.code !== APPROVAL_TYPE_CODES.SEND_BACK) {
        return true;
      }
      // Only show send back if there are eligible targets
      return sendBackTargets.length > 0;
    });

    // Sort by priority: Approve (app), Reject (rej), Send Back (sbk)
    const priorityOrder = {
      [APPROVAL_TYPE_CODES.APPROVED]: 1,
      [APPROVAL_TYPE_CODES.REJECTED]: 2,
      [APPROVAL_TYPE_CODES.SEND_BACK]: 3,
    };

    return filtered.sort((a, b) => {
      const priorityA = priorityOrder[a.ApprovalType?.code] || 999;
      const priorityB = priorityOrder[b.ApprovalType?.code] || 999;
      return priorityA - priorityB;
    });
  }, [children, sendBackTargets]);

  // Subform navigation using reusable hook
  const {
    activeIndex: activeSubformIndex,
    navigate: navigateSubform,
    reset: resetSubformNavigation,
    hasMultipleItems: hasMultipleSubforms,
  } = useSubformNavigation(activeForm?.subforms || []);

  // Reset subform navigation when form tab changes
  useEffect(() => {
    resetSubformNavigation();
  }, [activeTab, resetSubformNavigation]);

  const hasSubformComments = useMemo(
    () =>
      activeForm?.subforms?.some((sf) => subformCommentStatus[sf.id]) ?? false,
    [activeForm?.subforms, subformCommentStatus]
  );

  const hasAnyComment = visibleComments.length > 0 || hasSubformComments;

  const handleAction = useCallback(
    async (child) => {
      if (
        !hasAnyComment &&
        child.ApprovalType?.code === APPROVAL_TYPE_CODES.SEND_BACK
      ) {
        toast.warning(t('Please add at least one comment before submitting.'), {
          position: 'top-center',
        });
        return;
      }

      if (child.ApprovalType?.code === APPROVAL_TYPE_CODES.REJECTED) {
        setSelectedChild(child);
        setShowRejectionModal(true);
        return;
      }

      setIsProcessing(true);
      try {
        if (assessmentTabData?.callerRole === 'assessmentReviewer') {
          const ownAssignment = assessmentTabData.reviewers.find(
            (r) => r.isOwnSection
          );
          if (!ownAssignment?.assignmentId)
            throw new Error('No own assignment found');
          await submitAssessmentReview(ownAssignment.assignmentId, 'approved');
        } else {
          const finalAction =
            APPROVAL_TYPE_TO_ACTION[child.ApprovalType?.id] ||
            APPROVAL_TYPE_TO_ACTION[APPROVAL_TYPE_IDS.REJECTED];

          await switchProgress({
            action: finalAction,
            actionStageId: child.id,
            requestsProgressId: requestProgress.id,
            wfVersionId: child.wf_version_id,
          });
        }

        // Show appropriate success message based on action type
        const actionType = child.ApprovalType?.code;
        if (actionType === APPROVAL_TYPE_CODES.SEND_TO_ASSESSMENT) {
          toast.success(t('Request sent to assessment successfully'), {
            position: 'top-center',
          });
        } else if (actionType === APPROVAL_TYPE_CODES.APPROVED) {
          toast.success(t('Request approved successfully'), {
            position: 'top-center',
          });
        } else if (actionType === APPROVAL_TYPE_CODES.SEND_BACK) {
          toast.success(t('Request sent back successfully'), {
            position: 'top-center',
          });
        } else {
          toast.success(t('Review Submitted Successfully'), {
            position: 'top-center',
          });
        }
        navigate(-1);
      } catch (err) {
        console.error('Error submitting action:', err);
        toast.error(t('Failed to submit action'), {
          position: 'top-center',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [
      hasAnyComment,
      requestProgress,
      navigate,
      t,
      switchProgress,
      submitAssessmentReview,
      assessmentTabData,
    ]
  );

  const handleRejectionConfirm = useCallback(
    async (rejectionReason) => {
      setIsProcessing(true);
      try {
        if (assessmentTabData?.callerRole === 'assessmentReviewer') {
          const ownAssignment = assessmentTabData.reviewers.find(
            (r) => r.isOwnSection
          );
          if (!ownAssignment?.assignmentId)
            throw new Error('No own assignment found');
          await submitAssessmentReview(
            ownAssignment.assignmentId,
            'rejected',
            rejectionReason
          );
        } else {
          await switchProgress({
            action: APPROVAL_TYPE_TO_ACTION[APPROVAL_TYPE_IDS.REJECTED],
            actionStageId: selectedChild.id,
            requestsProgressId: requestProgress.id,
            wfVersionId: selectedChild.wf_version_id,
            rejectionReason,
          });
        }
        toast.success(t('Request Rejected Successfully'), {
          position: 'top-center',
        });
        setShowRejectionModal(false);
        navigate(-1);
      } catch (error) {
        console.error('Error rejecting request:', error);
        toast.error(t('Failed to reject the request. Please try again.'), {
          position: 'top-center',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [
      selectedChild,
      requestProgress,
      navigate,
      t,
      switchProgress,
      submitAssessmentReview,
      assessmentTabData,
    ]
  );

  // Get the current active subform
  const activeSubform = useMemo(() => {
    if (!activeForm?.subforms) return null;
    return activeForm.subforms[activeSubformIndex] || null;
  }, [activeForm, activeSubformIndex]);

  // Check if current subform is approved
  const isCurrentSubformApproved = useMemo(() => {
    if (!activeSubform?.id) return false;
    return !!activeFormApprovalStatus[activeSubform.id]?.approved;
  }, [activeSubform, activeFormApprovalStatus]);

  // Handle section approval
  const handleApproveSectionClick = useCallback(async () => {
    if (!activeSubform?.id) return;

    setIsApprovingSection(true);
    try {
      await handleApproveSection(activeSubform.id);
      toast.success(t('sectionApprovedSuccess'), {
        position: 'top-center',
      });
    } catch (error) {
      console.error('Error approving section:', error);
      toast.error(t('Failed to approve section'), {
        position: 'top-center',
      });
    } finally {
      setIsApprovingSection(false);
    }
  }, [activeSubform, handleApproveSection, t]);

  // Handle undo approval
  const handleUndoApprovalClick = useCallback(async () => {
    if (!activeSubform?.id) return;

    setIsApprovingSection(true);
    try {
      await handleUndoApproval(activeSubform.id);
      toast.success(t('sectionApprovalUndone'), {
        position: 'top-center',
      });
    } catch (error) {
      console.error('Error undoing approval:', error);
      toast.error(t('Failed to undo approval'), {
        position: 'top-center',
      });
    } finally {
      setIsApprovingSection(false);
    }
  }, [activeSubform, handleUndoApproval, t]);

  // Determine if section approval buttons should be shown
  // Must be assigned (same classification as Approvals.js) to approve sections
  const canApproveSection = useMemo(() => {
    return (
      isRequestAssigned &&
      requestProgress?.active &&
      activeForm &&
      canTakeActionOnForm(activeForm.id) &&
      activeSubform
    );
  }, [
    isRequestAssigned,
    requestProgress,
    activeForm,
    canTakeActionOnForm,
    activeSubform,
  ]);

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <div className="loading-container">
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );
  }

  const formTabConfigs = submittedForms.map((form, index) => ({
    key: form.id || index,
    label: form.name || `${t('Form')} ${index + 1}`,
  }));
  const approveTabConfigs = [
    ...formTabConfigs,
    ...(assessmentTabData
      ? [{ key: 'assessment', label: t('assessmentReviewers') }]
      : []),
    ...(linkedStudiesEnabled
      ? [{ key: 'linkedStudies', label: t('linkedStudies') }]
      : []),
    ...(showParentRequestTab
      ? [{ key: 'parentRequest', label: 'Relevant Requests' }]
      : []),
    { key: 'additionalDocuments', label: t('additionalDocuments') },
    { key: 'auditTrails', label: t('auditTrails') },
    { key: 'clarificationWaves', label: t('clarificationWaves.tabLabel') },
  ];

  return (
    <div className="form-approve-container">
      <StyledTabs value={activeTab} onChange={handleTabChange} truncate>
        {/* Relevant Requests is shown first; values keep the original
            indices so form indexing is unaffected. */}
        {approveTabConfigs
          .map((tab, index) => ({ ...tab, value: index }))
          .sort(
            (a, b) => (b.key === 'parentRequest') - (a.key === 'parentRequest')
          )
          .map((tab) => (
            <StarTab
              key={tab.key}
              value={tab.value}
              label={tab.label}
              isActive={tab.value === activeTab}
            />
          ))}
      </StyledTabs>

      {isFormTab && activeForm && (
        <div key={`form-content-${activeForm.id}`}>
          {attachEndUserProfileSnapshot ? (
            <FormSections
              subForms={activeForm.subforms || []}
              requestProgressId={requestProgress?.id}
              persona="admin"
              requestId={requestProgress?.request_id}
              filledForm={activeForm.filledForm}
              requestProgress={requestProgress}
              children={filteredChildren}
              handleAction={handleAction}
              roleName={null}
              setComments={setFormSectionsComments}
              isReadOnly
              enableAddComment={
                isRequestAssigned &&
                requestProgress?.active &&
                canTakeActionOnForm(activeForm.id)
              }
              hasHcpLicensesList={Boolean(
                activeForm?.has_hcp_licenses_list ||
                requestDetails?.hcpLicensesList?.id
              )}
              hcpProfileMenuEnabled
              hcpProfileMainMenuTabs={hcpProfileMainMenuTabs}
              renderHcpProfileMenuContent={renderHcpProfileMenuContent}
              formName={activeForm.name}
              showRequestBadges
            />
          ) : (
            <SubformNavigation
              items={activeForm.subforms || []}
              activeIndex={activeSubformIndex}
              onNavigate={navigateSubform}
              approvalStatus={activeFormApprovalStatus}
              commentStatus={visibilityAwareCommentStatus}
              headerSlot={
                requestProgress?.id ? (
                  <RequestBadges
                    requestProgressId={requestProgress.id}
                    fallbackRequestId={generatedId}
                    requestOutcomeStatus={requestOutcomeStatus}
                  />
                ) : null
              }
            >
              <FormCrafterListView
                key={`form-${activeForm.id}-${activeTab}-${activeSubformIndex}`}
                subforms={activeForm.subforms}
                filledForm={activeForm.filledForm}
                fileMetadata={activeForm.filledForm?.fileMetadata || []}
                comments={activeFormComments}
                setComments={setCommentsForActiveForm}
                enableAddComment={
                  isRequestAssigned &&
                  requestProgress?.active &&
                  canTakeActionOnForm(activeForm.id)
                }
                showHiddenFields={true}
                activeSubformIndex={
                  hasMultipleSubforms ? activeSubformIndex : undefined
                }
                formId={activeForm.id}
                requestId={requestProgress?.request_id}
                enableFieldWaves={true}
                requestProgress={requestProgress}
                requestDateEnded={requestDateEnded}
                currentUserEmail={currentUserEmail}
                onSubformCommentAdded={refreshSubformCommentStatus}
              />

              {canApproveSection && isRequestActive && (
                <SectionApprovalButton
                  isApproved={isCurrentSubformApproved}
                  onApprove={handleApproveSectionClick}
                  onUndoApprove={handleUndoApprovalClick}
                  loading={isApprovingSection}
                />
              )}
            </SubformNavigation>
          )}

          {isRequestAssigned &&
            isRequestActive &&
            requestProgress?.active &&
            canTakeActionOnForm(activeForm.id) &&
            filteredChildren?.length > 0 && (
              <FormActionButtons
                actionChildren={filteredChildren}
                onAction={handleAction}
                disabled={isProcessing}
                requireComment={false}
              />
            )}

          {(() => {
            const ownAssignment = assessmentTabData?.reviewers?.find(
              (r) => r.isOwnSection
            );
            if (
              assessmentTabData?.callerRole === 'assessmentReviewer' &&
              ownAssignment?.assessmentStatus === 'pending'
            ) {
              const assessmentActionChildren = [
                {
                  id: 'assess-approve',
                  ApprovalType: {
                    code: APPROVAL_TYPE_CODES.APPROVED,
                    id: APPROVAL_TYPE_IDS.APPROVED,
                    name: 'Approve',
                  },
                },
                {
                  id: 'assess-reject',
                  ApprovalType: {
                    code: APPROVAL_TYPE_CODES.REJECTED,
                    id: APPROVAL_TYPE_IDS.REJECTED,
                    name: 'Reject',
                  },
                },
              ];
              return (
                <FormActionButtons
                  actionChildren={assessmentActionChildren}
                  onAction={handleAction}
                  disabled={isProcessing}
                  requireComment={false}
                />
              );
            }
            return null;
          })()}
        </div>
      )}

      {assessmentTabData && activeTab === dynamicTabs.assessmentTabIndex && (
        <AssessmentReviewersTab
          initialData={assessmentTabData}
          currentUserEmail={currentUserEmail}
        />
      )}

      {linkedStudiesEnabled && activeTab === dynamicTabs.linkedStudiesIndex && (
        <LinkedStudiesView
          linkedStudies={linkedStudies}
          loading={linkedStudiesLoading}
          error={linkedStudiesError}
          detailPathPrefix="/admin/review"
        />
      )}

      {showParentRequestTab && activeTab === dynamicTabs.parentRequestIndex && (
        <>
          {hasParentRequests && (
            <ParentRequestInfo
              parentRequests={requestParents.parentRequests}
              detailPathPrefix="/admin/review"
            />
          )}
          {canHaveLinkedSubmissions && linkedSubmissions.total > 0 && (
            <LinkedSubmissionsView
              data={linkedSubmissions.data}
              total={linkedSubmissions.total}
              page={linkedSubmissions.page}
              onPageChange={linkedSubmissions.setPage}
              loading={linkedSubmissions.loading}
              error={linkedSubmissions.error}
              detailPathPrefix="/admin/review"
            />
          )}
        </>
      )}

      {activeTab === dynamicTabs.requestAuditTrailsIndex && (
        <RequestAuditTrails requestId={requestProgress?.request_id} />
      )}

      {activeTab === dynamicTabs.additionalDocumentsIndex && (
        <AdditionalDocuments
          requestId={requestProgress?.request_id}
          requestDateEnded={requestDateEnded}
          requestProgress={requestProgress}
          currentUserEmail={currentUserEmail}
        />
      )}

      {activeTab === dynamicTabs.requestClarificationWavesIndex && (
        <RequestClarificationWaves
          requestId={requestProgress?.request_id}
          requestProgressId={requestProgress?.id}
        />
      )}

      <RejectionReasonModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onConfirm={handleRejectionConfirm}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default FormApprove;
